import { memo, useMemo } from 'react';

import { countBoards, renderBoardTree } from '~/components/Modals/BoardTemplateModal/boardTemplateHelpers';
import { MAX_BOARDS_CREATE } from '~/components/Modals/BoardTemplateModal/constants';
import { type WorkspaceTemplateConfig } from '~/components/Modals/BoardTemplateModal/types';

export type BoardTemplateModalTemplatePreviewProps = {
  selectedTemplateId: string | null;
  templates: WorkspaceTemplateConfig[];
};

export const BoardTemplateModalTemplatePreview = memo(
  ({ selectedTemplateId, templates }: BoardTemplateModalTemplatePreviewProps) => {
    const selectedTemplate = useMemo(
      () => templates.find((t) => t.id === selectedTemplateId),
      [templates, selectedTemplateId],
    );
    const boardCount = useMemo(() => (selectedTemplate ? countBoards(selectedTemplate.boards) : 0), [selectedTemplate]);
    const exceedsLimit = boardCount > MAX_BOARDS_CREATE;

    if (!selectedTemplateId) {
      return (
        <div className="flex h-full w-1/2 items-center justify-center rounded-8 border border-grey-5 bg-grey-1 p-8 text-14 text-grey-9">
          Select a template to preview its structure
        </div>
      );
    }

    return (
      <div className="w-1/2">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-semibold text-14 text-grey-12">Template Structure:</h3>
          <span className="text-12 text-grey-11">{boardCount} boards</span>
        </div>
        {exceedsLimit && (
          <div className="mb-2 rounded-8 border border-red-6 bg-red-1 p-3 text-12 text-red-11">
            This template has {boardCount} boards, which exceeds the limit of {MAX_BOARDS_CREATE} boards per operation.
          </div>
        )}
        <div className="rounded-8 border border-grey-5 bg-grey-1 p-4">
          <div className="max-h-96 overflow-y-auto font-mono text-12">
            {renderBoardTree(selectedTemplate?.boards || [])}
          </div>
        </div>
      </div>
    );
  },
);

BoardTemplateModalTemplatePreview.displayName = 'BoardTemplateModalTemplatePreview';
